// Pause-eintragen-Dialog (inkl. wöchentlicher Wiederholung) —
// extrahiert aus AppointmentsPage.js (Refactoring 06/2026).
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toLocalYMD } from './calendarUtils';
import { Switch } from '@/components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { trainer_id: '', date: '', start: '09:00', end: '09:30', note: '', repeat_weekly: false, repeat_until: '' };

export const BreakDialog = ({ open, onOpenChange, trainers, currentDate, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Beim Öffnen Defaults setzen (Datum = aktueller Kalendertag,
  // Wiederholungs-Enddatum = +8 Wochen)
  useEffect(() => {
    if (open) {
      const untilDefault = new Date(currentDate);
      untilDefault.setDate(untilDefault.getDate() + 56);
      setForm({
        ...EMPTY_FORM,
        date: toLocalYMD(currentDate),
        repeat_until: toLocalYMD(untilDefault),
      });
    }
  }, [open, currentDate]);

  const handleSave = async () => {
    if (!form.trainer_id) { toast.error('Bitte Trainer wählen'); return; }
    if (!form.date || !form.start || !form.end) { toast.error('Bitte Datum und Zeiten angeben'); return; }
    if (form.end <= form.start) { toast.error('Ende muss nach Beginn liegen'); return; }
    if (form.repeat_weekly && (!form.repeat_until || form.repeat_until < form.date)) {
      toast.error('Bitte gültiges Enddatum für die Wiederholung wählen');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Lokale Zeit → ISO mit Zeitzonen-Offset (Backend speichert wie Termine)
      const startIso = new Date(`${form.date}T${form.start}:00`).toISOString();
      const endIso = new Date(`${form.date}T${form.end}:00`).toISOString();
      const res = await axios.post(`${API}/appointments/break`, {
        trainer_id: form.trainer_id,
        start_time: startIso,
        end_time: endIso,
        note: form.note.trim(),
        repeat_weekly: form.repeat_weekly,
        repeat_until: form.repeat_weekly ? form.repeat_until : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const cnt = res.data?.count || 1;
      toast.success(cnt > 1
        ? `${cnt} wiederkehrende Pausen eingetragen — werden zu Mindbody gespiegelt`
        : 'Pause eingetragen — wird zu Mindbody gespiegelt');
      onOpenChange(false);
      onSaved();
    } catch (e) {
      console.error('break save err', e);
      toast.error(e?.response?.data?.detail || 'Fehler beim Eintragen der Pause');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="break-dialog">
        <DialogHeader>
          <DialogTitle>☕ Pause eintragen</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Trainer</Label>
            <Select value={form.trainer_id} onValueChange={(v) => setForm(f => ({ ...f, trainer_id: v }))}>
              <SelectTrigger data-testid="break-trainer-select">
                <SelectValue placeholder="Trainer wählen" />
              </SelectTrigger>
              <SelectContent>
                {trainers.map(t => {
                  // trainers-State ist verschachtelt: { trainer: {...}, availability: {...} }
                  const tr = t.trainer || t;
                  return (
                    <SelectItem key={tr.id} value={tr.id}>
                      {tr.first_name} {tr.last_name}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Datum</Label>
            <Input type="date" value={form.date}
              onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
              data-testid="break-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Von</Label>
              <Input type="time" value={form.start}
                onChange={(e) => setForm(f => ({ ...f, start: e.target.value }))}
                data-testid="break-start-input" />
            </div>
            <div>
              <Label>Bis</Label>
              <Input type="time" value={form.end}
                onChange={(e) => setForm(f => ({ ...f, end: e.target.value }))}
                data-testid="break-end-input" />
            </div>
          </div>
          <div>
            <Label>Notiz (optional)</Label>
            <Input placeholder="z.B. Mittagspause, RAV…" value={form.note}
              onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
              data-testid="break-note-input" />
          </div>
          <div className="flex items-center justify-between rounded-md border p-2.5">
            <Label htmlFor="break-repeat-switch" className="cursor-pointer text-sm">
              🔁 Wöchentlich wiederholen
            </Label>
            <Switch id="break-repeat-switch"
              checked={form.repeat_weekly}
              onCheckedChange={(v) => setForm(f => ({ ...f, repeat_weekly: v }))}
              data-testid="break-repeat-switch" />
          </div>
          {form.repeat_weekly && (
            <div>
              <Label>Wiederholen bis (inkl.)</Label>
              <Input type="date" value={form.repeat_until}
                min={form.date}
                onChange={(e) => setForm(f => ({ ...f, repeat_until: e.target.value }))}
                data-testid="break-repeat-until-input" />
              <p className="text-xs text-gray-500 mt-1">
                Erstellt jede Woche am gleichen Wochentag eine Pause (max. 60 Wochen).
              </p>
            </div>
          )}
          <p className="text-xs text-gray-500">Die Pause blockiert die Buchbarkeit und wird automatisch zu Mindbody gespiegelt.</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-zinc-800 hover:bg-zinc-900 text-white" data-testid="break-save-btn">
            {saving ? 'Speichern…' : 'Pause eintragen'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
